import { budgetFormat } from "../../../utils/number/budget-format.ts"

/**
 * The money a material earns on one unit: its sale price less what the
 * supplier charges for it.
 *
 * Money, not a percentage: a per-unit figure the price list itself carries,
 * stated the way every other figure on this grid is.
 *
 * `null` means there is no margin to state: a material with no unit cost is
 * pass-through — the sale books income and nothing is owed to the supplier.
 *
 * Display only, and only on the price list. Nothing here reaches a finance
 * line: the two flows are independent, no line carries a margin, and the server
 * stores the two figures and derives nothing.
 */
export function materialUnitMargin(
  unitCost: string | number | null | undefined,
  unitSalePrice: string | number | null | undefined
): number | null {
  const blank = (figure: string | number | null | undefined) =>
    figure === null || figure === undefined || figure === ""
  // Both sides, not just the cost: `Number(null)` is 0 and passes `isFinite`,
  // so clearing the sale price mid-edit would read as a margin of minus the
  // cost rather than as nothing to state.
  if (blank(unitCost) || blank(unitSalePrice)) return null

  const cost = Number(unitCost)
  const sale = Number(unitSalePrice)

  if (!Number.isFinite(cost) || !Number.isFinite(sale)) return null

  return sale - cost
}

export function formatMaterialMargin(margin: number): string {
  return budgetFormat(margin, { minimumFractionDigits: 2 })
}
